import React, { useEffect, useMemo, useRef, useState } from 'react'
import { getNextState } from '../../../utils/scene/utils/state/state'
import useStateReducer from '../../../utils/scene/react/hooks/useStateReducer'
import { controls } from '../../../constants/three'
import {
  hiddenControlsStates,
  hiddenSceneStates,
  bustersData,
  settings,
  ignoreNextStates,
  states,
} from '../../../constants/game-match3-settings'
import { levels } from '../../../constants/match3Level'
import classNames from 'classnames'
import { useDispatch } from 'react-redux'
import user, { useUser } from '../../../redux/reducer/user'
import Match3Controls from './Match3Controls'
import { CSSTransition } from 'react-transition-group'
import Match3Busters from './Match3Busters'
import Match3Note from './Match3Note'
import CustomButton from '../../features/customButton/ui/main/CustomButton'
import content, { useContent } from '../../../redux/reducer/content'
import { soundManager } from '../../../utils/scene/sound/SoundManager'
import { useRequests } from '../../../redux/reducer/requests'
import { useGameRules } from '../../../hooks/useGameRules'
import { useGamesModal } from '../../../hooks/useGamesModal'
import AppLoader from '../../features/appLoader/AppLoader'
import { GameWrapper } from '../../features/gameWrapper/GameWrapper'
import { useTranslation, defaultStore, stores } from '../../../translations'
import { getConfig } from '../../../utils/getConfig'
import { eventSubscription } from '../../../utils/events/eventSubscription'
import { usePreloadedGameWrapper } from '../../../hooks/usePreloadedGameWrapper'
import { upperFirstLetter } from '../../../utils/helpers/helpers'

const gameWinScore = 50

const Match3 = React.forwardRef(function Match3({ callbacks }, ref) {
  const dispatch = useDispatch()
  const { games } = useUser()
  const { gamesSettings, state: appState } = useContent()
  const { profile } = useUser()
  const {
    authData: { isWeb },
  } = useRequests()
  const preloadGameWrapper = usePreloadedGameWrapper('match3') // предзагруженный wrapper

  const { rulesModalCallback } = useGameRules()
  const { t } = useTranslation()
  const { cdn: cdnBaseUrl } = getConfig()

  // Контейнер куда будет монтироваться сanvas
  const containerRef = useRef()
  const [wrapper, setWrapper] = useState(null)
  const [state, setState] = useState('loadManifest')
  const [score, setScore] = useState(0)
  const [isAnimate, setIsAnimate] = useState(false)
  const [step, setStep] = useState(0)
  const [activeItem, setActiveItem] = useState(null)
  const [trigger, setTrigger] = useState(false)
  const [boosterCount, setBoosterCount] = useState(0)

  // Количество завершённых игр (определяет, какой уровень открывать)
  const level =
    gamesSettings?.find((item) => upperFirstLetter(appState) === item.name)?.completedGames ?? 0
  const isReady = !hiddenControlsStates.includes(state)
  const isHiddenScene = hiddenSceneStates.includes(state)
  const currentLevel = useMemo(() => level % levels.length ?? 0, [level])

  const data = useMemo(() => ({ inUnmounted: false }), [])

  // Данные по бустерам берем из профиля
  const updatedBustersData = profile?.boosters
    ? bustersData.map((item) => {
        const booster = profile.boosters.find((b) => b.systemName === item.name)

        if (booster) {
          return { ...item, label: t(item.label), count: booster.count }
        }

        return { ...item, label: t(item.label) }
      })
    : bustersData

  const [boosters, setBoosters] = useState(() =>
    updatedBustersData.reduce((acc, item) => {
      acc[item.name] = item.count

      return acc
    }, {}),
  )

  // Колбэк при проигрыше
  const loseCallback = () => {
    callbacks?.gameEnd({ is_win: 0, score, boosters, booster_count: boosterCount })
  }

  // Колбэк при победе
  const winCallback = () => {
    callbacks?.gameEnd({
      is_win: 1,
      score: gameWinScore,
      money: gameWinScore,
      boosters,
      booster_count: boosterCount,
    })
  }

  // Управление модальными окнами (пауза, ожидание загрузки, обработка проигрыша)
  const { openPauseModal, isPending } = useGamesModal({
    game: 'match3',
    callbacks,
    loseCallback,
    winCallback,
    resetCallback: () => setBoosterCount(0),
    setGameState: setState,
  })

  useEffect(() => {
    wrapper?.triggerResize()

    return () => dispatch(content.actions.setMainSound('main'))
  }, [level])

  useEffect(() => {
    rulesModalCallback()

    return () => (data.inUnmounted = true)
  }, [])

  useEffect(() => {
    if (isAnimate) {
      return
    }

    if (state === 'losing') {
      loseCallback(false)
    }

    if (state === 'win') {
      winCallback()
    }
  }, [isAnimate, state])

  // Указываем что делаем при переходе в состояние игры (при паузе показываем модальное окно и тп)
  useStateReducer(
    {
      loading(loadingPromise) {
        dispatch(content.actions.setMainSound('match3'))
        dispatch(
          user.thunks.m3LoadGame({
            loadingPromise,
            onComplete() {
              setState('loadComplete')
            },
          }),
        )
      },
      reset() {
        setTrigger(!trigger)
        setScore(0)
        setStep(0)
      },
      playing() {
        soundManager.onPlay('startGame')
      },
      paused() {
        openPauseModal()
      },
    },
    ignoreNextStates,
    (state) => setState(getNextState(states, state)),
    state,
    wrapper,
  )

  useEffect(() => {
    if (isAnimate) {
      setActiveItem(null)
    }
  }, [isAnimate])

  // Подставляем предзагруженный wrapper из стора в локальный компонент
  useEffect(() => {
    if (preloadGameWrapper && !wrapper) {
      setWrapper(preloadGameWrapper)
    }
  }, [preloadGameWrapper]) // Срабатывает, когда предзагруженный wrapper становится доступен

  // Инициализация wrapper после его загрузки (настройка сложности и цели монет)
  useEffect(() => {
    if (!wrapper || !containerRef.current) {
      return
    }

    wrapper.storage.currentLevel = currentLevel
    wrapper.storage.games = games
    data.wrapper = wrapper

    wrapper.storage.gameData = settings
    // В test добавляем window.wrapper для отладки; без флага сцена использует локальный wrapper.
    if (process.env.TAMAGOTCHI_BUILD_TARGET === 'test') window.wrapper = wrapper
    wrapper.hidden = false
    wrapper.appendContainer(containerRef.current)

    return () => wrapper && (wrapper.hidden = true)
  }, [wrapper])

  // Подписка на события игрового движка (потеря жизни, изменение счёта, монет и тд)
  useEffect(() => {
    if (!wrapper) {
      return
    }

    const { eventBus } = wrapper

    return eventSubscription({
      target: eventBus,
      callbacksBus: [
        {
          event: 'add-step',
          callback({ data: { stepCount, points } }) {
            const currentLevel = data.wrapper.storage.currentLevel
            const moves = levels[currentLevel].maxSteps
            const targetScore = levels[currentLevel].targetScore

            setStep(() => {
              if (stepCount >= moves && targetScore > points) {
                setState('losing')
              }

              return stepCount
            })
          },
        },
        {
          event: 'add-points',
          callback({ data: { points } }) {
            const targetScore = levels[data.wrapper.storage.currentLevel].targetScore

            setScore(() => {
              if (points >= targetScore) {
                setState('win')
              }

              return Math.min(points, targetScore)
            })
          },
        },
        {
          event: 'buster-apply',
          callback({ data: { name, count } }) {
            setBoosterCount((prev) => ++prev)
            setBoosters((prevBoosters) => ({
              ...prevBoosters,
              [name]: count,
            }))
          },
        },
        {
          event: 'animate-state',
          callback({ data: { isAnimate } }) {
            setIsAnimate(isAnimate)
          },
        },
      ],
    })
  }, [wrapper])

  return (
    <GameWrapper>
      <div
        className={classNames('match3', `match3_level-${level + 1}`)}
        ref={ref}
      >
        {defaultStore === stores.ae && (
          <img
            src={`${cdnBaseUrl}/images/match3/me/logo.png`}
            className={classNames('match3__title', { app_margin: !isWeb })}
            alt=''
          />
        )}
        <Match3Note activeItem={activeItem} />
        <Match3Controls
          isVisible={isReady}
          isWeb={isWeb}
          list={controls({ step, level, score, settings: levels[currentLevel] })}
        />
        <div className={`match3__scene ${isWeb ? '' : 'app_margin'}`}>
          <CSSTransition
            in={!isHiddenScene}
            timeout={5000}
            classNames={'match3__scene-block'}
            appear={true}
          >
            <div
              className={'match3__scene-block'}
              ref={containerRef}
            />
          </CSSTransition>
        </div>
        {wrapper && (
          <Match3Busters
            isAnimate={isAnimate}
            isVisible={isReady}
            wrapper={wrapper}
            list={updatedBustersData}
            activeItem={activeItem}
            setActiveItem={setActiveItem}
            trigger={trigger}
          />
        )}
        <CustomButton
          className={`match3__close button_pause ${isWeb ? '' : 'app_margin'}`}
          icon={'buttons/pause'}
          onClick={() => setState('paused')}
        />

        {isPending && <AppLoader />}
      </div>
    </GameWrapper>
  )
})

export default Match3
Match3.propTypes = {}
